import React, { memo, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { css, styled } from '@compiled/react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { colors } from '@atlaskit/theme';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { REMOVE_GROUP_GUARDS, NEW_GROUP_ID } from '../../../common/constants';
import type { TicketType, RemoveGroupGuard } from '../../../common/types';
import useGetTicketTypesByGroup from '../../../controllers/get-ticket-types-by-group/index.tsx';
import useUpdateTicketTypesInGroup from '../../../controllers/update-ticket-types-in-group/index.tsx';
import TicketTypeItem, { ticketTypeItemWrapperSelector } from './ticket-type-item';

type Props = {
	groupId: number;
	onRemoveGroup?: (arg1: RemoveGroupGuard) => void;
};
type ListProps = Props & {
	items: TicketType[];
};

export const List = memo<ListProps>(({ groupId, items, onRemoveGroup }: ListProps) => {
	const { remove } = useUpdateTicketTypesInGroup(groupId);
	const isLastItem = items.length === 1;
	const handleRemove = useCallback(
		(id: number) => {
			if (onRemoveGroup && NEW_GROUP_ID !== groupId && isLastItem) {
				onRemoveGroup({
					reason: REMOVE_GROUP_GUARDS.oneTicketTypeLeft,
				});
			} else {
				remove(id);
			}
		},
		[groupId, isLastItem, onRemoveGroup, remove],
	);

	return items.map<ReactNode>(({ id, name, iconUrl }: TicketType, index: number) => (
		<Draggable key={id} draggableId={`draggable-ticket-type-${groupId}-${id}`} index={index}>
			{({ draggableProps, dragHandleProps, innerRef }, { isDragging }) => (
				<RowWrapper>
					<DragWrapper {...draggableProps} ref={innerRef} isDragging={isDragging}>
						<TicketTypeItem
							id={id}
							name={name}
							iconUrl={iconUrl}
							dragHandleProps={dragHandleProps}
							onRemove={handleRemove}
						/>
					</DragWrapper>
				</RowWrapper>
			)}
		</Draggable>
	));
});

const TicketTypeList = memo<Props>(({ groupId, onRemoveGroup }: Props) => {
	const ticketTypes: TicketType[] | null = useGetTicketTypesByGroup(groupId);
	const { reorder } = useUpdateTicketTypesInGroup(groupId);
	const listRef = useRef<HTMLElement | null>(null);
	const lengthRef = useRef<number>(ticketTypes ? ticketTypes.length : 0);

	useEffect(() => {
		if (listRef.current && ticketTypes) {
			const { length } = ticketTypes;
			// Scroll to the bottom when user add more ticket types to the group,
			// this is to make sure the last added ticket type will always visible
			if (lengthRef.current < length) {
				listRef.current.scrollTop = listRef.current.scrollHeight;
			}
			lengthRef.current = length;
		}
	}, [ticketTypes]);

	if (ticketTypes) {
		return (
			<DragDropContext onDragEnd={reorder}>
				<Droppable droppableId={`droppable-ticket-types-${groupId}`}>
					{(droppable, { isDraggingOver }) => (
						<TicketTypeListWrapper
							{...droppable.droppableProps}
							ref={(ref) => {
								listRef.current = ref;
								droppable.innerRef(ref);
							}}
							isDraggingOver={isDraggingOver}
						>
							<List groupId={groupId} items={ticketTypes} onRemoveGroup={onRemoveGroup} />
							{droppable.placeholder}
						</TicketTypeListWrapper>
					)}
				</Droppable>
			</DragDropContext>
		);
	}

	return null;
});

export default TicketTypeList;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TicketTypeListWrapper = styled.div<{ isDraggingOver: boolean }>({
	maxHeight: '400px',
	marginTop: token('space.100', '8px'),
	overflow: 'auto',
	position: 'relative',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ isDraggingOver }) =>
		isDraggingOver &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			backgroundColor: token('color.background.selected', colors.B50),
			/* Comestic artificial margin on the drop area */
			'&::after': {
				content: '',
				position: 'absolute',
				top: 0,
				bottom: 0,
				left: 0,
				right: 0,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				borderLeft: `${gridSize * 3}px solid ${token('color.border.inverse', colors.N0)}`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				borderRight: `${gridSize}px solid ${token('color.border.inverse', colors.N0)}`,
			},

			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			[ticketTypeItemWrapperSelector]: {
				pointerEvents: 'none',
			},
		}),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RowWrapper = styled.div({
	paddingLeft: token('space.300', '24px'),
	paddingRight: token('space.100', '8px'),

	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('elevation.surface.raised', colors.N20),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`&:hover ${ticketTypeItemWrapperSelector}`]: {
		backgroundColor: 'transparent',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DragWrapper = styled.div<{
	isDragging: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ref: (element: HTMLElement | null) => any;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ isDragging }) =>
		isDragging &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css({
			boxShadow: token(
				'elevation.shadow.raised',
				'0px 8px 16px rgba(9, 30, 66, 0.25), 0px 0px 1px rgba(9, 30, 66, 0.31)',
			),
			borderRadius: '3px',
		}),
});
